import { useState, useCallback, useEffect } from 'react';

// API_BASE should point to the main web app (apps/web) which hosts the git API
const API_BASE = process.env.REACT_APP_API_BASE || 'http://localhost:3000';

/**
 * Hook for interacting with the Git versioning API
 * 
 * @param {Object} config - Configuration options
 * @param {string} config.brandId - Brand UUID for per-brand sandbox
 * @param {string} config.brandSlug - Brand slug for sandbox path resolution
 * @returns {Object} Git state and operations
 */
export function useGitHistory({ brandId, brandSlug } = {}) {
  const [commits, setCommits] = useState([]);
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Determine the API endpoint based on whether we're in brand mode
  const getGitEndpoint = useCallback((params = '') => {
    if (brandId) {
      return `${API_BASE}/api/brands/${brandId}/sandbox/git${params ? `?${params}` : ''}`;
    }
    // Fallback to legacy endpoint for global mode
    return `${API_BASE}/api/site-agent/git${params ? `?${params}` : ''}`;
  }, [brandId]);

  /**
   * Fetch commit history
   */
  const fetchHistory = useCallback(async (limit = 50) => {
    if (!brandId) {
      // Brand not selected yet - silently skip
      return [];
    }
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(getGitEndpoint(`action=history&limit=${limit}`), {
        credentials: 'include',
      });
      if (!response.ok) {
        const text = await response.text();
        let err = { error: 'Unknown error' };
        try { err = JSON.parse(text); } catch {}
        throw new Error(err.error || `HTTP ${response.status}`);
      }
      const data = await response.json();
      // API returns array directly for history action
      const commitList = Array.isArray(data) ? data : (data.commits || []);
      setCommits(commitList);
      return commitList;
    } catch (err) {
      setError(err.message);
      return [];
    } finally {
      setLoading(false);
    }
  }, [brandId, getGitEndpoint]);

  /**
   * Fetch git status
   */
  const fetchStatus = useCallback(async () => {
    if (!brandId) {
      // Brand not selected yet - silently skip
      return null;
    }
    try {
      const response = await fetch(getGitEndpoint('action=status'), {
        credentials: 'include',
      });
      if (!response.ok) {
        const text = await response.text();
        let err = { error: 'Unknown error' };
        try { err = JSON.parse(text); } catch {}
        throw new Error(err.error || `HTTP ${response.status}`);
      }
      const data = await response.json();
      // API returns array directly for status action
      const statusData = Array.isArray(data) ? data : (data.status || data);
      setStatus(statusData);
      return statusData;
    } catch (err) {
      setError(err.message);
      return null;
    }
  }, [brandId, getGitEndpoint]);

  /**
   * Get diff for a specific commit
   */
  const getCommitDiff = useCallback(async (commitHash) => {
    try {
      const response = await fetch(getGitEndpoint(`action=show&commit=${commitHash}`), {
        credentials: 'include',
      });
      if (!response.ok) {
        const text = await response.text();
        let err = { error: 'Unknown error' };
        try { err = JSON.parse(text); } catch {}
        throw new Error(err.error || `HTTP ${response.status}`);
      }
      return await response.json();
    } catch (err) {
      setError(err.message);
      return null;
    }
  }, [getGitEndpoint]);

  /**
   * Get diff between two commits
   */
  const getDiff = useCallback(async (fromRef, toRef = 'HEAD') => {
    try {
      const response = await fetch(getGitEndpoint(`action=diff&from=${fromRef}&to=${toRef}`), {
        credentials: 'include',
      });
      if (!response.ok) {
        const text = await response.text();
        let err = { error: 'Unknown error' };
        try { err = JSON.parse(text); } catch {}
        throw new Error(err.error || `HTTP ${response.status}`);
      }
      return await response.json();
    } catch (err) {
      setError(err.message);
      return null;
    }
  }, [getGitEndpoint]);

  /**
   * Revert a specific commit
   */
  const revertCommit = useCallback(async (commitHash) => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(getGitEndpoint(), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ action: 'revert', commit: commitHash }),
      });
      if (!response.ok) {
        const text = await response.text();
        let err = { error: 'Unknown error' };
        try { err = JSON.parse(text); } catch {}
        throw new Error(err.error || `HTTP ${response.status}`);
      }
      const result = await response.json();
      // Refresh history after revert
      await fetchHistory();
      return result;
    } catch (err) {
      setError(err.message);
      return null;
    } finally {
      setLoading(false);
    }
  }, [fetchHistory, getGitEndpoint]);

  /**
   * Reset to a specific commit (destructive!)
   */
  const resetToCommit = useCallback(async (commitHash) => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(getGitEndpoint(), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ action: 'reset', commit: commitHash }),
      });
      if (!response.ok) {
        const text = await response.text();
        let err = { error: 'Unknown error' };
        try { err = JSON.parse(text); } catch {}
        throw new Error(err.error || `HTTP ${response.status}`);
      }
      const result = await response.json();
      // Refresh history after reset
      await fetchHistory();
      return result;
    } catch (err) {
      setError(err.message);
      return null;
    } finally {
      setLoading(false);
    }
  }, [fetchHistory, getGitEndpoint]);

  /**
   * Create a manual commit
   */
  const commit = useCallback(async (message, files = null) => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(getGitEndpoint(), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ action: 'commit', message, files }),
      });
      if (!response.ok) {
        const text = await response.text();
        let err = { error: 'Unknown error' };
        try { err = JSON.parse(text); } catch {}
        throw new Error(err.error || `HTTP ${response.status}`);
      }
      const result = await response.json();
      // Refresh history after commit
      await fetchHistory();
      return result;
    } catch (err) {
      setError(err.message);
      return null;
    } finally {
      setLoading(false);
    }
  }, [fetchHistory, getGitEndpoint]);

  // Clear error
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  return {
    commits,
    status,
    loading,
    error,
    fetchHistory,
    fetchStatus,
    getCommitDiff,
    getDiff,
    revertCommit,
    resetToCommit,
    commit,
    clearError,
  };
}

export default useGitHistory;

